import {inject, Injectable, OnDestroy} from '@angular/core';
import {OnlineOfflineService} from "../onlineOffline/online-offline.service";
import {StorageService} from "../storage/storage.service";
import {AuthService} from "../auth/auth.service";
import {FailedRequestEntity} from "./dirty-data.interceptor";
import {ErrorService} from "../error/error.service";
import {LoggerService} from "../logging/logger.service";
import {ObservabilityService} from "../observability/observability.service";


@Injectable({
  providedIn: 'root'
})
export class DirtyDataWriterService implements OnDestroy {
  private readonly onlineOfflineService = inject(OnlineOfflineService);
  private readonly storageService = inject(StorageService);
  private readonly authenticationServer = inject(AuthService);
  private readonly errorService = inject(ErrorService);
  private readonly logger = inject(LoggerService);
  private readonly observabilityService = inject(ObservabilityService);

  private intervalId: any;
  private online: boolean = false;
  private processInFlight: boolean = false;
  private readonly worker: Worker;

  constructor() {
    this.worker = new Worker('/assets/dirty-data-writer.js');
    this.worker.onmessage = (event) => {
      this.handleCallBack(event.data).then(() => {
        this.logger.info('Callback processing finished');
      });
    };
  }

  async initialize() {
    const isOnline = await this.onlineOfflineService.isOnline();

    this.observabilityService.trackProcessStarted('DirtyDataWriter', {
      online: isOnline
    });

    this.registerToEvents(this.onlineOfflineService);
    this.online = isOnline;

    this.observabilityService.trackServiceInitialized('DirtyDataWriter', {
      online: isOnline
    });
  }

  ngOnDestroy() {
    this.observabilityService.trackProcessStopped('DirtyDataWriter', {
      processInFlight: this.processInFlight,
      online: this.online
    });

    this.stopPeriodicTask();
    if (this.worker) {
      this.worker.terminate();
    }
  }

  public async findFailedRequest(failedRequest: FailedRequestEntity): Promise<FailedRequestEntity | null> {
    return await this.storageService.getItem<FailedRequestEntity>(failedRequest.id, failedRequest.type);
  }

  public async storeFailedRequest(failedRequest: FailedRequestEntity): Promise<void> {
    await this.storageService.setItem(failedRequest.id, failedRequest);

  }

  private async removeFailedRequest(failedRequest: FailedRequestEntity) {
    await this.storageService.removeItem(failedRequest.id, failedRequest.type);
  }

  private registerToEvents(onlineOfflineService: OnlineOfflineService) {
    onlineOfflineService.connectionChanged.subscribe(online => {
      this.observabilityService.trackDataSyncConnectionChanged(
        this.online ? 'online' : 'offline',
        online ? 'online' : 'offline',
        {
          previousState: this.online,
          processInFlight: this.processInFlight
        }
      );

      this.online = online;
      if (online) {
        this.startPeriodicTask();
      } else {
        this.stopPeriodicTask();
      }
    });
  }

  private async handleCallBack(data: any) {

    try {
      if (data.completed) {
        this.logger.info('Worker has completed all tasks');
        this.processInFlight = false;

        const queueSize = await this.getQueueSize();
        this.observabilityService.trackDataSyncBatchCompleted(`batch_${Date.now()}`, queueSize, {
          remainingQueueSize: queueSize
        });
      }  else {
        const failedRequest = data?.data ? await this.findFailedRequest(new FailedRequestEntity(data.data.url, data.data.method, data.data.body)) : null;

        if (failedRequest) {
          if (data.status === 200) {
            await this.removeFailedRequest(failedRequest);

            this.observabilityService.trackDataSyncRequestSucceeded(failedRequest.id, {
              url: failedRequest.url,
              method: failedRequest.method,
              retries: failedRequest.retries,
              processingTime: this.calculateProcessingTime(failedRequest.startTime)
            });

          } else if (data.status > 200) {
            // The request was successful, but the server returned an application error
            failedRequest.retries++;

            if (failedRequest.retries > 50) {
              await this.removeFailedRequest(failedRequest);
              this.registerErrorMessage(failedRequest.body);

              this.observabilityService.trackDataSyncRequestAbandoned(failedRequest.id, {
                url: failedRequest.url,
                method: failedRequest.method,
                retries: failedRequest.retries,
                status: data.status,
                statusText:  data.statusText,
                reason: 'max_retries_exceeded'
              });
            } else {
              await this.storeFailedRequest(failedRequest);

              this.observabilityService.trackDataSyncRequestRetryScheduled(failedRequest.id, failedRequest.retries, {
                url: failedRequest.url,
                method: failedRequest.method,
                status: data.status,
                statusText: data.statusText
              });
            }
          } else {
            this.logger.info(data.statusText);

            this.observabilityService.trackDataSyncRequestFailed(`Request failed with status ${data.status}`, {
              requestId: failedRequest.id,
              url: failedRequest.url,
              method: failedRequest.method,
              retries: failedRequest.retries,
              status: data.status,
              statusText: data.statusText
            });
          }
        }
      }

    } catch (error) {
      this.logger.error('Error processing callback', error, data);
      this.observabilityService.captureException(error, {
        context: 'DataSync.CallbackProcessingError',
        data: data
      });
    }
  }

  private registerErrorMessage(body: any) {
    if (!body) return;

    const message = body instanceof FormData
      ? 'Unable to upload photo'
      : `Unable to upload ${body.type}`;

    this.errorService.registerError(message);
  }

  /**
   * Calculates the processing time in seconds from the startTime.
   * Handles cases where startTime may be null, undefined, a string (from JSON
   * deserialisation), or not a valid Date object.
   */
  private calculateProcessingTime(startTime: Date | string | null | undefined): number | string {
    if (!startTime) {
      return 'N/A';
    }

    try {
      // Handle string dates (from JSON deserialisation)
      const dateValue = startTime instanceof Date ? startTime : new Date(startTime);

      // Check if the date is valid
      if (isNaN(dateValue.getTime())) {
        return 'N/A';
      }

      return Math.floor((Date.now() - dateValue.getTime()) / 1000);
    } catch {
      return 'N/A';
    }
  }

  private postDirtyDataToWorker(): void {
    if (this.authenticationServer.isLoggedIn() && this.online && !this.processInFlight) {
      // always drain post requests
      this.drainDirtyDataQueue().then((postItemsQueued: boolean) => {
        if (postItemsQueued) {
          this.logger.debug('Processing dirty data');
        }
      });
    } else {
      this.logger.debug('Not processing dirty data due to offline status or no authentication');
    }
  }

  private async drainDirtyDataQueue(): Promise<boolean> {
    const startTime = Date.now();

    try {
      let failedRequests: FailedRequestEntity[] = await this.storageService.getAllItems(`FailedRequestEntity`);

      if (failedRequests && failedRequests.length != 0) {
        this.observabilityService.trackDataSyncQueueDrainStarted({
          queueSize: failedRequests.length,
          online: this.online,
          authenticated: this.authenticationServer.isLoggedIn()
        });

        this.logger.info(`Found ${failedRequests.length} failed requests to process`);

        // sort list by time from oldest to newest
        failedRequests.sort((a, b) => a.time - b.time);

        try {
          this.processInFlight = true;
          this.worker.postMessage({
            items: failedRequests,
            authToken: this.authenticationServer.getAuthToken()
          });

          return true;
        } catch (error) {
          this.processInFlight = false;
          this.logger.error('Error posting dirty data to worker', error);

          this.observabilityService.captureException(error, {
            context: 'DataSync.WorkerPostError',
            batchSize: failedRequests.length
          });
        }
      }

      return false;
    } catch (error) {
      this.observabilityService.captureException(error, {
        context: 'DataSync.QueueDrainError',
        processingTime: Date.now() - startTime
      });
      return false;
    }
  }

  private async getQueueSize(): Promise<number> {
    try {
      const failedRequests = await this.storageService.getAllItems(`FailedRequestEntity`);
      return failedRequests?.length ?? 0;
    } catch (error) {
      this.observabilityService.captureException(error, {
        context: 'DataSync.QueueSizeError'
      });
      return 0;
    }
  }

  startPeriodicTask(): void {
    this.logger.debug('Starting dirty data periodic task');

    this.observabilityService.trackProcessStarted('DirtyDataWriter', {
      online: this.online,
      processInFlight: this.processInFlight
    });

    this.intervalId = setInterval(() => {
      this.postDirtyDataToWorker();
    }, 30000); // 30 seconds
  }

  stopPeriodicTask(): void {
    if (this.intervalId) {
      this.logger.info('Stopping dirty data periodic task');

      this.observabilityService.trackProcessStopped('DirtyDataWriter', {
        online: this.online,
        processInFlight: this.processInFlight
      });

      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}
